/*
 * theme/assets/section-compatibility-picker.js
 * Phase 8.5 atomic-push file 5/5
 *
 * Vanilla JS custom element. Mirrors theme/assets/global.js:960 (VariantSelects).
 * Zero third-party deps. State machine for 3-step picker + collapsed-CTA drawer wrapper.
 *
 * D-VARIANT-001 contract:
 *  - Reads option names from rendered <variant-selects> at runtime; no hardcoded option-name strings
 *  - Variant flip = select.value = ... + dispatchEvent(new Event('change', { bubbles: true }))
 *  - Slipstream defaults to "Option <letter> - 4 Pack"; Gatekeeper writes only "Option <letter>" (exact)
 */

class CompatibilityPicker extends HTMLElement {
  constructor() {
    super();
    this.state = 'INITIAL';
    this.selectedBrandHandle = null;
    this.selectedTentId = null;
    this.isPdp = this.dataset.isPdp === 'true';
    this.gridColumns = { brand: 6, tent: 2 };
    this._previouslyFocused = null;
    this._searchDebounce = null;
  }

  connectedCallback() {
    this.computeGridColumns();
    window.addEventListener('resize', () => this.computeGridColumns());
    // MUST bind before bindDrawerToggle/bindKeyboardNav. All three listen for Escape
    // on `this`, and listeners fire in registration order — the fit-request panel has
    // to get first refusal so Escape closes it instead of resetting the picker behind it.
    this.bindFitRequest();
    this.bindDrawerToggle();
    this.bindBrandClicks();
    this.bindTentClicks();
    this.bindBackButtons();
    this.bindResultCta();
    this.bindBrandSearch();
    this.bindKeyboardNav();
    this.transitionTo('INITIAL');
    this.applyDeepLink();
  }

  // === Deep link ===

  /**
   * Open the picker straight onto a brand from the URL: ?brand=<handle>.
   *
   * The tent brand ribbon links every logo here, so clicking "iKamper" on the
   * homepage should land on the iKamper tent list rather than the brand grid the
   * visitor just chose from.
   *
   * The handle is validated against a card that actually exists rather than
   * passed to selectBrand() directly. selectBrand() sets state and transitions
   * regardless of whether the brand is real, so an unknown or hand-edited param
   * would move the picker to BRAND_SELECTED with an empty tent grid -- a dead end
   * with no way back except the browser button.
   */
  applyDeepLink() {
    let handle = '';
    try {
      handle = new URLSearchParams(window.location.search).get('brand') || '';
      if (!handle && window.location.hash.startsWith('#brand=')) {
        handle = decodeURIComponent(window.location.hash.slice('#brand='.length));
      }
    } catch (e) { return; }
    if (!handle) return;

    handle = handle.trim().toLowerCase();
    const card = this.querySelector(
      `.ssg-compat-picker__brand-card[data-brand-handle="${CSS.escape(handle)}"]`
    );
    if (!card) return;

    // On a product page the drawer is display:none until opened, and a hidden
    // subtree cannot be scrolled to or focused. Open first, exactly as the
    // fit-request flow does, then select.
    if (this.dataset.isPdp === 'true') this.openDrawer();
    this.selectBrand(handle);

    // Let the step transition paint before scrolling, otherwise the target is
    // still the old step and the page lands in the wrong place.
    requestAnimationFrame(() => {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  computeGridColumns() {
    const w = window.innerWidth;
    if (w < 640)       { this.gridColumns = { brand: 2, tent: 1 }; }
    else if (w < 1024) { this.gridColumns = { brand: 4, tent: 2 }; }
    else               { this.gridColumns = { brand: 6, tent: 2 }; }
  }

  // === Drawer (PDP only) ===

  bindDrawerToggle() {
    const trigger = this.querySelector('[data-action="open-drawer"]');
    const closeBtn = this.querySelector('[data-action="close-drawer"]');
    if (trigger) trigger.addEventListener('click', () => this.openDrawer());
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeDrawer());
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.drawerState === 'open' && this.isPdp) {
        e.preventDefault();
        this.closeDrawer();
      }
    });
  }

  openDrawer() {
    if (!this.isPdp) return;
    this._previouslyFocused = document.activeElement;
    this.dataset.drawerState = 'open';
    const trigger = this.querySelector('[data-action="open-drawer"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.body.classList.add('ssg-compat-picker-drawer-open');
    }
    this.transitionTo('INITIAL');
    requestAnimationFrame(() => {
      const search = this.querySelector('[data-brand-search]');
      const stepBrand = this.querySelector('.ssg-compat-picker__step--brand');
      if (search) search.focus();
      else if (stepBrand) stepBrand.focus();
    });
  }

  closeDrawer() {
    if (!this.isPdp) return;
    this.dataset.drawerState = 'closed';
    const trigger = this.querySelector('[data-action="open-drawer"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('ssg-compat-picker-drawer-open');
    const focusTarget = this._previouslyFocused || trigger;
    if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
  }

  // === State machine ===

  transitionTo(newState) {
    const previousState = this.state;
    this.state = newState;
    this.classList.remove('ssg-compat-picker--step-brand', 'ssg-compat-picker--step-tent', 'ssg-compat-picker--step-result');
    if (newState === 'INITIAL')        this.classList.add('ssg-compat-picker--step-brand');
    if (newState === 'BRAND_SELECTED') {
      this.classList.add('ssg-compat-picker--step-tent');
      this.revealTentGridForSelectedBrand();
    }
    if (newState === 'TENT_SELECTED')  {
      this.classList.add('ssg-compat-picker--step-result');
      this.populateResultPanel();
      if (!this.isPdp) this.revealResultLinks();
    }
    this.refreshRovingTabIndex();

    if (previousState !== 'INITIAL' || newState !== 'INITIAL') {
      this.announceStepTransition(newState);
      this.focusActiveStep(newState);
    }
  }

  announceStepTransition(newState) {
    const announcer = this.querySelector('[data-announcer]');
    if (!announcer) return;
    let message = '';
    if (newState === 'INITIAL') {
      message = 'Step 1 of 3: pick your tent brand.';
    } else if (newState === 'BRAND_SELECTED') {
      const brandCard = this.querySelector(`.ssg-compat-picker__brand-card[data-brand-handle="${this.selectedBrandHandle}"]`);
      const brandName = brandCard ? (brandCard.querySelector('.ssg-compat-picker__brand-name') || brandCard.querySelector('.ssg-compat-picker__brand-fallback')) : null;
      const label = brandName ? brandName.textContent.trim() : 'selected brand';
      message = `Step 2 of 3: pick your ${label} tent model.`;
    } else if (newState === 'TENT_SELECTED') {
      const tentBtn = this.querySelector(`[data-tent-id="${this.selectedTentId}"]`);
      const letter = tentBtn ? tentBtn.dataset.optionLetter : '';
      message = letter ? `Step 3 of 3: matched Option ${letter}.` : 'Step 3 of 3: match found.';
    }
    announcer.textContent = '';
    setTimeout(() => { announcer.textContent = message; }, 50);
  }

  focusActiveStep(newState) {
    let target = null;
    if (newState === 'INITIAL')        target = this.querySelector('.ssg-compat-picker__step--brand');
    if (newState === 'BRAND_SELECTED') target = this.querySelector('.ssg-compat-picker__step--tent');
    if (newState === 'TENT_SELECTED')  target = this.querySelector('.ssg-compat-picker__step--result');
    if (target && typeof target.focus === 'function') {
      requestAnimationFrame(() => {
        target.focus({ preventScroll: false });
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }
  }

  revealTentGridForSelectedBrand() {
    const wraps = this.querySelectorAll('.ssg-compat-picker__tent-grid-wrap');
    wraps.forEach(w => {
      if (w.dataset.brandHandle === this.selectedBrandHandle) w.removeAttribute('hidden');
      else                                                    w.setAttribute('hidden', '');
    });
  }

  populateResultPanel() {
    const tentBtn = this.querySelector(`[data-tent-id="${this.selectedTentId}"]`);
    if (!tentBtn) return;
    const letter = tentBtn.dataset.optionLetter;
    const letterEl = this.querySelector('[data-result-letter]');
    if (letterEl) letterEl.textContent = letter;

    const nameEl = tentBtn.querySelector('.ssg-compat-picker__tent-name');
    const versionEl = tentBtn.querySelector('.ssg-compat-picker__tent-version');
    const name = nameEl ? nameEl.textContent.trim() : '';
    const version = versionEl ? versionEl.textContent.trim() : '';
    const tentLabel = version && version !== name ? `${name} — ${version}` : name;
    const tentEl = this.querySelector('[data-result-tent-name]');
    if (tentEl) tentEl.textContent = tentLabel;
  }

  revealResultLinks() {
    // Result tiles are keyed on the OPTION LETTER, not the tent id. Which mount a
    // tent needs is decided solely by its option_letter, and there are three
    // letters across 135 tents -- so the section renders three tile blocks rather
    // than 135, and the match is letter-to-letter.
    const tentBtn = this.querySelector(`[data-tent-id="${this.selectedTentId}"]`);
    const letter = tentBtn ? tentBtn.dataset.optionLetter : null;

    const blocks = this.querySelectorAll('.ssg-compat-picker__result-products');
    blocks.forEach(b => {
      if (letter && b.dataset.optionLetter === letter) b.removeAttribute('hidden');
      else                                             b.setAttribute('hidden', '');
    });

    // Older markup shipped one link pair per tent. Keep honouring it so a theme
    // that still has those stubs does not lose its result links mid-deploy.
    this.querySelectorAll('.ssg-compat-picker__result-link-pair').forEach(p => {
      if (p.dataset.tentId === this.selectedTentId) p.removeAttribute('hidden');
      else                                          p.setAttribute('hidden', '');
    });
  }

  // === Selection actions ===

  selectBrand(handle) {
    this.selectedBrandHandle = handle;
    this.querySelectorAll('.ssg-compat-picker__brand-card').forEach(b => {
      b.setAttribute('aria-pressed', b.dataset.brandHandle === handle ? 'true' : 'false');
    });
    this.transitionTo('BRAND_SELECTED');
  }

  selectTent(tentId) {
    this.selectedTentId = tentId;
    this.querySelectorAll('.ssg-compat-picker__tent-card').forEach(b => {
      b.setAttribute('aria-pressed', b.dataset.tentId === tentId ? 'true' : 'false');
    });
    this.transitionTo('TENT_SELECTED');
    if (this.isPdp) {
      const tentBtn = this.querySelector(`[data-tent-id="${tentId}"]`);
      const letter = tentBtn && tentBtn.dataset.optionLetter;
      if (letter) {
        this.flipVariant(letter);
        // Phase 8.5.4: result panel stays open after variant flip so the user
        // can verify Option + tent. Close is now manual via the X button.
      }
    }
  }

  reset(target) {
    if (target === 'brand') {
      this.selectedBrandHandle = null;
      this.selectedTentId = null;
      this.transitionTo('INITIAL');
    } else if (target === 'tent') {
      this.selectedTentId = null;
      this.transitionTo('BRAND_SELECTED');
    }
  }

  // === Variant flip (D-VARIANT-001) ===

  flipVariant(optionLetter) {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) {
      console.warn('[compat-picker] variant-selects element not found on PDP — flip skipped');
      return;
    }

    const targetExact  = `Option ${optionLetter}`;
    const targetPrefix = `Option ${optionLetter} - 4 Pack`;

    for (const select of variantSelects.querySelectorAll('select')) {
      const options = Array.from(select.options);
      const matchingOption =
        options.find(o => o.value === targetExact) ||
        options.find(o => o.value === targetPrefix) ||
        options.find(o => o.value.startsWith(`Option ${optionLetter} - `));

      if (matchingOption) {
        select.value = matchingOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.scrollAndPulse(variantSelects);
        return;
      }
    }
    console.warn('[compat-picker] no <select> option matched letter', optionLetter, '— flip skipped');
  }

  scrollAndPulse(el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.add('ssg-compat-picker__variant-highlight');
    setTimeout(() => el.classList.remove('ssg-compat-picker__variant-highlight'), 400);
  }

  // === Click bindings ===

  bindBrandClicks() {
    this.querySelectorAll('.ssg-compat-picker__brand-card').forEach(card => {
      card.addEventListener('click', () => this.selectBrand(card.dataset.brandHandle));
    });
  }

  bindTentClicks() {
    this.querySelectorAll('.ssg-compat-picker__tent-card').forEach(card => {
      card.addEventListener('click', () => this.selectTent(card.dataset.tentId));
    });
  }

  bindBackButtons() {
    this.querySelectorAll('.ssg-compat-picker__back').forEach(btn => {
      btn.addEventListener('click', () => this.reset(btn.dataset.backTarget));
    });
  }

  bindResultCta() {
    const cta = this.querySelector('[data-action="flip-variant"]');
    if (!cta) return;
    cta.addEventListener('click', () => {
      const tentBtn = this.querySelector(`[data-tent-id="${this.selectedTentId}"]`);
      const letter = tentBtn && tentBtn.dataset.optionLetter;
      if (letter) {
        this.flipVariant(letter);
        setTimeout(() => this.closeDrawer(), 600);
      }
    });
  }

  // === Brand search (debounced filter) ===

  bindBrandSearch() {
    const input = this.querySelector('[data-brand-search]');
    if (!input) return;
    input.addEventListener('input', () => {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => this.applyBrandFilter(input.value), 100);
    });
  }

  applyBrandFilter(query) {
    const q = (query || '').trim().toLowerCase();
    const items = this.querySelectorAll('.ssg-compat-picker__brand-item');
    let visibleCount = 0;
    items.forEach(item => {
      const text = item.textContent.trim().toLowerCase();
      const handle = (item.querySelector('[data-brand-handle]')?.dataset.brandHandle || '').toLowerCase();
      const match = q === '' || text.includes(q) || handle.includes(q);
      if (match) {
        item.removeAttribute('hidden');
        visibleCount += 1;
      } else {
        item.setAttribute('hidden', '');
      }
    });
    const empty = this.querySelector('[data-brand-empty]');
    if (empty) {
      if (visibleCount === 0) empty.removeAttribute('hidden');
      else                    empty.setAttribute('hidden', '');
    }
  }

  // === Fit request panel (CONV-FIT-06) ===
  //
  // Replaces the 3 mailto: dead-ends. One panel per section instance, opened by any
  // of the 35 triggers the section renders on live data (1 brand-search-empty + 34
  // per-brand "not seeing your tent"). The brand travels with the trigger.

  bindFitRequest() {
    this.fitPanel = this.querySelector('[data-fit-request]');
    if (!this.fitPanel) return;

    this.querySelectorAll('[data-open-fit-request]').forEach((btn) => {
      btn.addEventListener('click', () => this.openFitRequest(btn.dataset.brand || ''));
    });

    const closeBtn = this.fitPanel.querySelector('[data-close-fit-request]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeFitRequest());

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || this.fitPanel.hasAttribute('hidden')) return;
      e.preventDefault();
      // Stop the drawer/step Escape handlers registered after this one.
      e.stopImmediatePropagation();
      this.closeFitRequest();
    });

    // Loading state. Native form POST leaves the page, so the button would otherwise
    // sit there looking clickable for the whole round trip.
    const form = this.fitPanel.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        const submit = this.fitPanel.querySelector('[data-fit-submit]');
        if (!submit) return;
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        const idle = submit.querySelector('[data-fit-submit-idle]');
        const pending = submit.querySelector('[data-fit-submit-pending]');
        if (idle) idle.setAttribute('hidden', '');
        if (pending) pending.removeAttribute('hidden');
      });
    }

    // Shopify redirects back here after a contact-form POST. If the panel stayed shut
    // the shopper would never see whether it worked, so reopen on either outcome.
    //
    // openDrawer() FIRST and not optionally: on a PDP the drawer is display:none until
    // opened, and this panel lives inside it. Skipping this returns the shopper to a
    // page where the confirmation exists in the DOM, is invisible, and cannot take
    // focus -- which reads to them as "nothing happened". No-op off the PDP.
    const outcome = this.fitPanel.querySelector('[data-fit-form-success], [data-fit-form-error]');
    if (outcome) {
      this.openDrawer();
      this.openFitRequest('');
      if (typeof outcome.focus === 'function') outcome.focus();
    }
  }

  openFitRequest(brandName) {
    if (!this.fitPanel) return;
    this._fitPreviouslyFocused = document.activeElement;
    this.fitPanel.removeAttribute('hidden');
    this.classList.add('ssg-compat-picker--fit-request-open');

    // Prefill the brand. Prefer the trigger's own brand; fall back to whatever brand
    // the picker currently has selected. Never clobber something the shopper typed.
    const brandInput = this.fitPanel.querySelector('[data-fit-brand-input]');
    if (brandInput && !brandInput.value) {
      let name = brandName;
      if (!name && this.selectedBrandHandle) {
        const card = this.querySelector(`.ssg-compat-picker__brand-card[data-brand-handle="${this.selectedBrandHandle}"]`);
        const label = card && (card.querySelector('.ssg-compat-picker__brand-name') || card.querySelector('.ssg-compat-picker__brand-fallback'));
        if (label) name = label.textContent.trim();
      }
      if (name) brandInput.value = name;
    }

    const heading = this.fitPanel.querySelector('.ssg-compat-picker__heading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }

    const announcer = this.querySelector('[data-announcer]');
    if (announcer) {
      announcer.textContent = '';
      setTimeout(() => { announcer.textContent = 'Fit request form. Tell us what tent you have.'; }, 50);
    }
  }

  closeFitRequest() {
    if (!this.fitPanel) return;
    this.fitPanel.setAttribute('hidden', '');
    this.classList.remove('ssg-compat-picker--fit-request-open');
    const target = this._fitPreviouslyFocused;
    if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
  }

  // === Keyboard nav (UI-SPEC §"Keyboard Navigation") ===

  bindKeyboardNav() {
    this.addEventListener('keydown', e => this.onKeyDown(e));
    this.refreshRovingTabIndex();
  }

  refreshRovingTabIndex() {
    const stepBrand = this.querySelector('.ssg-compat-picker__step--brand');
    const stepTent  = this.querySelector('.ssg-compat-picker__step--tent');
    [stepBrand, stepTent].forEach(step => {
      if (!step) return;
      const cards = step.querySelectorAll('.ssg-compat-picker__brand-card, .ssg-compat-picker__tent-card');
      cards.forEach((c, i) => c.setAttribute('tabindex', i === 0 ? '0' : '-1'));
    });
  }

  onKeyDown(e) {
    const navKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Escape'];
    if (!navKeys.includes(e.key)) return;
    const focused = document.activeElement;

    if (e.key === 'Escape') {
      if (this.state === 'BRAND_SELECTED') { e.preventDefault(); this.reset('brand'); return; }
      if (this.state === 'TENT_SELECTED')  { e.preventDefault(); this.reset('tent');  return; }
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      if (focused && focused.classList.contains('ssg-compat-picker__brand-card')) {
        e.preventDefault(); this.selectBrand(focused.dataset.brandHandle); return;
      }
      if (focused && focused.classList.contains('ssg-compat-picker__tent-card')) {
        e.preventDefault(); this.selectTent(focused.dataset.tentId); return;
      }
      return;
    }

    if (!focused || (!focused.classList.contains('ssg-compat-picker__brand-card') && !focused.classList.contains('ssg-compat-picker__tent-card'))) return;

    const isBrand = focused.classList.contains('ssg-compat-picker__brand-card');
    const cols = isBrand ? this.gridColumns.brand : this.gridColumns.tent;
    const grid = isBrand ? this.querySelector('.ssg-compat-picker__brand-grid')
                         : focused.closest('.ssg-compat-picker__tent-grid');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('button')).filter(b => !b.closest('[hidden]'));
    const idx = cards.indexOf(focused);
    if (idx < 0) return;

    let nextIdx = idx;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % cards.length;
    if (e.key === 'ArrowLeft')  nextIdx = (idx - 1 + cards.length) % cards.length;
    if (e.key === 'ArrowDown')  nextIdx = Math.min(idx + cols, cards.length - 1);
    if (e.key === 'ArrowUp')    nextIdx = Math.max(idx - cols, 0);

    if (nextIdx !== idx) {
      e.preventDefault();
      cards[idx].setAttribute('tabindex', '-1');
      cards[nextIdx].setAttribute('tabindex', '0');
      cards[nextIdx].focus();
    }
  }
}

if (!customElements.get('compatibility-picker')) {
  customElements.define('compatibility-picker', CompatibilityPicker);
}
